/**
 * The group/section permission matrix inside the role editor (mockup
 * `#matrix`) — collapsible group rows (chevron, 44px target) each with a
 * "Set all" `LevelControl`, and per-section rows underneath. Pulled out of
 * RoleEditor.tsx to keep that file focused on draft/save/delete state.
 */
import { ADMIN_SECTIONS, ADMIN_SECTION_GROUPS, levelFor, type AccessLevel, type RolePermissions } from '@platform/shared-types';
import { LevelControl } from './LevelControl';
import { sectionsInGroup, groupLevel, countLevels } from './roleMath';

type V = AccessLevel | null;

function findSection(id: string) {
  return ADMIN_SECTIONS.find((s) => s.id === id);
}
function rowLabelMatches(id: string, query: string) {
  const s = findSection(id);
  return !!s && s.label.toLowerCase().includes(query);
}

export function PermissionMatrix({
  perms, query, openGroups, onToggleGroup, maxFor, disabled, onSetLevel, onSetGroupLevel,
}: {
  perms: RolePermissions;
  query: string;
  openGroups: Set<string>;
  onToggleGroup: (group: string) => void;
  maxFor: (sectionId: string) => V;
  disabled: boolean;
  onSetLevel: (sectionId: string, v: V) => void;
  onSetGroupLevel: (group: string, v: V) => void;
}) {
  const groupMaxRank = (group: string): V => {
    let best: V = null;
    for (const id of sectionsInGroup(group)) {
      const m = maxFor(id);
      if (m === 'edit') return 'edit';
      if (m === 'view') best = 'view';
    }
    return best;
  };

  return (
    <div className="divide-y divide-stone-100 text-stone-900">
      {ADMIN_SECTION_GROUPS.map((group) => {
        const allIds = sectionsInGroup(group);
        const visibleIds = query ? allIds.filter((id) => rowLabelMatches(id, query)) : allIds;
        if (visibleIds.length === 0) return null;
        const counts = countLevels(perms, allIds);
        const gLevel = groupLevel(perms, group);
        // A non-empty search always shows its matching groups expanded,
        // regardless of the group's own open/closed state.
        const isOpen = query.length > 0 || openGroups.has(group);
        return (
          <div key={group}>
            <div className="px-4 py-3 flex flex-wrap items-center gap-3 bg-stone-50/70">
              <button
                type="button"
                onClick={() => onToggleGroup(group)}
                aria-expanded={isOpen}
                className="flex-1 min-w-[160px] text-left font-semibold text-sm min-h-[44px] flex items-center gap-2"
              >
                <span className="text-stone-400" aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
                {group}
                <span className="font-normal text-xs text-stone-500">{counts.edit} Edit · {counts.view} View · {counts.off} Off</span>
              </button>
              <span className="text-[11px] text-stone-500 hidden sm:inline">Set all</span>
              <LevelControl
                value={gLevel === 'mixed' ? null : gLevel}
                mixed={gLevel === 'mixed'}
                max={groupMaxRank(group)}
                disabled={disabled}
                onChange={(v) => onSetGroupLevel(group, v)}
              />
            </div>
            <div hidden={!isOpen}>
              {visibleIds.map((id) => {
                const section = findSection(id);
                if (!section) return null;
                return (
                  <div key={id} className="pl-10 pr-4 py-2 flex flex-wrap items-center gap-3">
                    <p className="flex-1 min-w-[160px] text-sm">
                      {section.label}
                      {section.sensitive && (
                        <span title="Sensitive section" className="ml-1 text-[11px] px-1.5 py-0.5 rounded bg-[#FFF1F3] text-[#9F1239]">🛡 Sensitive</span>
                      )}
                    </p>
                    <LevelControl
                      value={levelFor(perms, id)}
                      max={maxFor(id)}
                      disabled={disabled}
                      onChange={(v) => onSetLevel(id, v)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default PermissionMatrix;
